import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteExtCustomColumnQuery,
    getExtCustomColumnQuery,
    setExtCustomColumnQuery,
    updateExtCustomColumnQueriesOrder,
    setOpenModal,
} from '../../store/extCustomColumnQueries/actions';
import IconButton from '../../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const ExtCustomColumnQueriesContext = createContext();

export const useExtCustomColumnQueries = () => useContext(ExtCustomColumnQueriesContext);

export const ExtCustomColumnQueriesProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { ExtCustomColumnQueriesList, loading, errors, openModal } = useSelector((state) => state.ExtCustomColumnQueries);
    const { extractionViewOnly } = useSelector((state) => state.Extractions);

    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");

    const extCustomColumnQueries = useMemo(() => {
        return ExtCustomColumnQueriesList?.data?.map(
            (item, index) => ({
                id: `${item._id}_${index}`, // Using _id + index for unique identification
                _id: item._id,
                key: item.key,
                title: item.title,
                query: item.query,
                fields: item.fields,
                originalIndex: index + 1,
            })
        ) || [];
    }, [ExtCustomColumnQueriesList]);

    const pagination = null; // Pagination disabled
        
    const handlePageChange = useCallback(() => {}, []); // Pagination disabled

    // Handle row reordering
    const handleRowReorder = useCallback((reorderedData) => {
        // Prepare data for API call
        const orderData = reorderedData.map((item, index) => ({
            _id: item._id,
            priority: index + 1
        }));
        
        // Call API to persist the new order
        dispatch(updateExtCustomColumnQueriesOrder(orderData));
    }, [dispatch]);

    // Function to get unique ID for each row
    const getRowId = useCallback((row) => {
        return row.id?.toString() || row._id?.toString() || '';
    }, []);

    const editExtCustomColumnQuery = useCallback((id) => {
        // Clear any previous data first
        dispatch(setExtCustomColumnQuery(null));
        // Open modal immediately
        dispatch(setOpenModal(true));
        // Fetch the data (will update extCustomColumnQuery when complete)
        dispatch(getExtCustomColumnQuery(id));
    }, [dispatch]);

    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const columnHelper = useMemo(() => createColumnHelper(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('originalIndex', { 
            header: '#', 
            cell: info => info.row.index + 1 // Show current position after reordering
        }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    <IconButton 
                        disabled={extractionViewOnly}
                        variant="action_default" 
                        size="icon" 
                        Icon={PencilSquareIcon} 
                        onClick={() => editExtCustomColumnQuery(info.row.original._id)} 
                    />
                    <IconButton 
                        disabled={extractionViewOnly}
                        variant="action_default" 
                        size="icon" 
                        Icon={TrashIcon} 
                        onClick={() => askDeleteConfirmation(info.row.original._id)} 
                    />
                </>
            ),
        }),
    ], [askDeleteConfirmation, columnHelper, editExtCustomColumnQuery, extractionViewOnly]);

    const table = useReactTable({
        data: extCustomColumnQueries,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: getRowId,
    });

    const openAddModal = useCallback(() => {
        dispatch(setExtCustomColumnQuery(null));
        dispatch(setOpenModal(true));
    }, [dispatch]);

    const handleCloseModal = useCallback(() => {
        dispatch(setOpenModal(false));
        dispatch(setExtCustomColumnQuery(null));
    }, [dispatch]);

    const handleCloseDeleteConfirmation = useCallback(() => {
        setShowDeleteConfirmationDialog(false);
        setToDeleteId("");
    }, []);

    const handleDelete = useCallback(() => {
        dispatch(deleteExtCustomColumnQuery(toDeleteId));
        setToDeleteId("");
        setShowDeleteConfirmationDialog(false);
    }, [dispatch, toDeleteId]);

    return (
        <ExtCustomColumnQueriesContext.Provider value={{
            extCustomColumnQueries,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editExtCustomColumnQuery,
            openAddModal,
            openModal,
            handleCloseModal,
            handleCloseDeleteConfirmation,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
            handleRowReorder,
            getRowId,
        }}>
            {children}
        </ExtCustomColumnQueriesContext.Provider>
    );
};